<template>
  <section class="c mapbox">
    <mapbox-component
      :access-token="accessToken"
      :map-options="{
        style: mapStyle,
        center: center,
        zoom: zoom
      }"
      :geolocate-control="geolocateControl"
      :scale-control="scaleControl"
      :fullscreen-control="fullscreenControl"
      @map-init="mapInitialized"
      @map-load="mapLoaded"
      @map-zoom="emitEvent('zoom', map)"
      @map-drag="emitEvent('drag', map)"
      @map-zoomstart="emitEvent('zoom-start')"
      @map-zoomend="emitEvent('zoom-end')"
      @map-dragstart="emitEvent('drag-start')"
      @map-dragend="emitEvent('drag-end')"
      @map-rotate="emitEvent('rotate')"
      @map-rotatestart="emitEvent('rotate-start')"
      @map-rotateend="emitEvent('rotate-end')"
      @map-click="onMapClick"
    />
    <transition name="fade">
      <div
        v-show="isLoading"
        class="loading"
      >
        <v-progress-linear :indeterminate="true" />
      </div>
    </transition>
    <div v-if="!overview" class="tools">
      <v-btn
        round
        :class="edit ? 'primary' : ''"
        @click="edit = !edit"
      >
        <v-icon>edit</v-icon>
      </v-btn>
    </div>
  </section>
</template>

<script>
import MapboxComponent from 'mapbox-gl-vue';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import { mapGetters } from 'vuex';
import axios from 'axios';
import { IdGenerator } from '../../utils/index';

export default {
  name: 'Mapbox',

  components: {
    MapboxComponent,
  },

  props: {
    center: {
      type: Array,
      required: false,
      default() {
        return [9.663738, 54.236640];
      },
    },
    zoom: {
      type: Number,
      default: 16,
    },
    accessToken: {
      type: String,
      required: true,
    },
    mapStyle: {
      type: String,
      required: true,
    },
    loadHandling: {
      type: Boolean,
      default: false,
    },
    geolocateControl: {
      type: Object,
      default() {
        return {
          show: true,
          position: 'top-left',
        };
      },
    },
    scaleControl: {
      type: Object,
      default() {
        return {
          show: true,
          position: 'top-left',
        };
      },
    },
    fullscreenControl: {
      type: Object,
      default() {
        return {
          show: false,
          position: 'top-left',
        };
      },
    },
    houses: {
      type: Array,
      default() {
        return [];
      },
    },
    nodes: {
      type: Array,
      default() {
        return [];
      },
    },
    overview: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      houseMarkerSet: [],
      nodeMarkerSet: [],
      overviewMarkerSet: [],
      linesSet: [],
      lines: [],
      isLoading: true,
      mapIsLoaded: false,
      layerSet: [],
      coordsArr: [],
      edit: false,
      editingNode: null,
      editingMapNode: null,
    };
  },
  computed: mapGetters(['showLines']),
  watch: {
    center() {
      this.setPosition(this.map);
    },
    zoom() {
      this.setPosition(this.map);
    },
    nodes() {
      if (!this.overview) {
        this.setNodes(this.map);
      }
    },
    lines() {
      if (this.lines.length > 0 && this.mapIsLoaded) {
        this.setLines(this.map);
      }
    },
    coordsArr() {
      if (this.coordsArr.length > 0) {
        this.calculateBbox();
      }
    },
    overview(newVal) {
      if (!newVal) {
        this.setLines(this.map);
        this.showOverviewMarker(false);
      } else {
        this.showOverviewMarker(true);
      }
    },
  },
  methods: {
    mapLoaded(map) {
      this.emitEvent('load', map);
      this.mapIsLoaded = true;
      this.$nextTick(() => {
        if (!this.overview) {
          this.setNodes(map);
          this.setLines(map);
        } else {
          this.showOverviewMarker(true);
        }
        this.isLoading = false;
      });
    },
    mapInitialized(map) {
      const Observer = Object.getPrototypeOf(this.$data.__ob__).constructor;
      map.__ob__ = new Observer({});
      this.map = map;
      this.setPosition(map);
      this.emitEvent('init', map);
    },
    setPosition(map) {
      map.setCenter(this.center);
      map.setZoom(this.zoom);
    },
    setNodes(map) {
      this.nodeMarkerSet.map((marker) => {
        marker.remove();
      });
      this.linesSet.map((lineId) => {
        this.map.removeLayer(lineId);
      });
      this.nodeMarkerSet = [];
      this.coordsArr = [];
      if (this.nodes.length > 0) {
        this.nodes.map((node) => {
          if (node.parent !== null) {
            this.lines.push({
              coords: [node.position.coordinates, node.parentPosition],
              error: node.error,
              parent_color: node.parent_color_code,
            });
          }
          // do not render, because same location as trafo, so we cannot click..
          if (node.type !== 'Abgangsfeld') {
            this.coordsArr.push(node.position.coordinates);
            const el = this.$el.querySelector(`#node-${node.id}`);
            if (!el) {
              const el = document.createElement('div');
              el.id = `node-${node.id}`;
              el.classList.add('marker');
              el.classList.add('is-node');
              let nodeMarkerIcon = 'home';
              if (node.type === 'Trafostation') nodeMarkerIcon = 'power_input';
              else if (node.type === 'Abgangsfeld') nodeMarkerIcon = 'flash_on';
              else if (node.type === 'KVS') nodeMarkerIcon = 'transform';
              else if (node.type === 'Hausanschluss') nodeMarkerIcon = 'home';
              if (node.error !== 'error-1') {
                el.innerHTML = `
                  <i class="material-icons">
                    ${nodeMarkerIcon}
                  </i>
                  <div class="label">${node.ms_link_number}</div>
                `;
              } else {
                el.innerHTML = `
                  <i class="material-icons">
                    ${nodeMarkerIcon}
                  </i>
                  <div class="label">${node.ms_link_number} / ${node.errorMsg}</div>
                `;
              }

              if (node.hasDevice) el.classList.add('has-device');
              else el.classList.remove('has-device');

              if (node.parent === null) el.classList.add('is-main-station');

              el.classList.add(`is-${node.type.toLowerCase()}`);

              if (node.error === 'error') el.classList.add('has-error');
              else el.classList.remove('has-error');

              if (node.error === 'warning') el.classList.add('has-warning');
              else el.classList.remove('has-warning');

              if (node.isParentError) el.classList.add('is-parent-error');
              else el.classList.remove('is-parent-error');

              const mapNode = new mapboxgl.Marker(el)
                .setLngLat(node.position.coordinates)
                .addTo(map);
              mapNode._id = node.id;
              this.nodeMarkerSet.push(mapNode);

              el.addEventListener('click', () => {
                if (this.edit) {
                  setTimeout(() => {
                    this.editingNode = node;
                    this.editingMapNode = mapNode;
                  }, 200);
                } else if (node.type !== 'Hausanschluss') {
                  this.emitEvent('endpoint-click', node);
                }
              });
            }
          }
        });
      }
    },
    setLines(map) {
      this.linesSet = [];
      if (this.lines.length > 0 && this.showLines && false) {
        this.lines.map((line) => {
          const lineId = IdGenerator.uniqueID();
          map.addLayer({
            id: `connection-line-${lineId}`,
            type: 'line',
            source: {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: line.coords,
                },
              },
            },
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': `${line.parent_color}`,
              'line-width': 2,
            },
          });
          this.linesSet.push(`connection-line-${lineId}`);
        });
      } else if (this.showLines) {
        map.addLayer({
          id: 'connection-lines',
          type: 'line',
          source: {
            type: 'vector',
            url: 'https://api.maptiler.com/tiles/26b36d1e-a7c6-430e-987f-e5719c321ddf/tiles.json?key=EiCgsIZ0YBbXRdNSx4X8',
          },
          'source-layer': 'kabel',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': 'black',
            'line-width': 2,
          },
        });
      }
      this.lines = [];
    },
    inBounds(point, bounds) {
      const lng = (point.lng - bounds._ne.lng) * (point.lng - bounds._sw.lng) < 0;
      const lat = (point.lat - bounds._ne.lat) * (point.lat - bounds._sw.lat) < 0;
      return lng && lat;
    },
    calculateBbox() {
      const lineString = turf.lineString(this.coordsArr);
      const bbox = turf.bbox(lineString);
      const routeName = this.$route.name;
      this.map.fitBounds(bbox, {
        padding: routeName !== 'dashboard' ? 96 : 128,
      });
      this.coordsArr = [];
    },
    emitEvent(eventName, args) {
      this.$emit(eventName, args);
    },
    onMapClick(map, evt) {
      if (this.editingNode) {
        const { lng, lat } = evt.lngLat;
        axios.put(`/api/endpoints/${this.editingNode.id}`, {
          endpoint: {
            position: {
              type: 'Point',
              coordinates: [lng, lat],
            },
          },
        }).then(() => {
          this.editingMapNode.setLngLat({ lng, lat });
          this.editingNode = null;
          this.editingMapNode = null;
        });
      }
    },
    removeAllSources() {
      this.linesSet.map((lineId) => {
        this.map.removeLayer(lineId);
      });
      this.linesSet = [];
      this.lines = [];
      this.nodeMarkerSet.map((marker) => {
        marker.remove();
      });
      this.nodeMarkerSet = [];
      this.houseMarkerSet.map((marker) => {
        marker.remove();
      });
      this.houseMarkerSet = [];
      this.map.removeLayer('connection-lines');
    },
    showOverviewMarker(bool) {
      if (bool) {
        this.overviewMarkerSet.map((marker) => {
          marker.remove();
        });
        this.$store.state.gridAreas.areas.map((area) => {
          const el = this.$el.querySelector(`#area-${area.id}`);
          if (!el) {
            const el = document.createElement('div');
            el.id = `area-${area.id}`;
            el.classList.add('marker');
            el.classList.add('is-area');
            if (area.error !== 'error-1') el.innerHTML = `${area.numberOfEndpoints}<div class="label">${area.label}</div>`;
            else el.innerHTML = `${area.numberOfEndpoints}<div class="label">${area.label} / ${area.errorMsg}</div>`;

            if (area.parent === null) el.classList.add('is-main-station');

            el.classList.add(`is-${area.type}`);

            if (area.error === 'error') el.classList.add('has-error');
            else el.classList.remove('has-error');

            if (area.error === 'warning') el.classList.add('has-warning');
            else el.classList.remove('has-warning');

            el.addEventListener('click', () => {
              this.$router.push(`area/${area.id}`);
            });

            const maparea = new mapboxgl.Marker(el)
              .setLngLat(area.position.coordinates)
              .addTo(this.map);
            maparea._id = area.id;
            this.overviewMarkerSet.push(maparea);
            this.coordsArr.push(area.position.coordinates);
            if (this.coordsArr.length >= 2) {
              this.calculateBbox();
            }
          }
        });
      }
    },
  },
};
</script>
<style scoped>
.tools {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 100;
}
</style>
